import { useEffect, useRef, useState } from 'react';
import type { QuestionId } from '../../core/model/brands.ts';
import type { QuestionReport, ReportReason } from '../../core/model/session.ts';
import { useI18n } from '../../i18n/index.tsx';
import { Button } from '../../ui/primitives.tsx';

const REASONS: readonly ReportReason[] = [
  'incorrect-answer',
  'ambiguous-question',
  'incorrect-explanation',
  'outdated-content',
  'conversion-error',
  'translation-error',
  'other',
];

/**
 * Reporting a problem with the content.
 *
 * Deliberately not the same thing as marking for review, which is a note to yourself about a
 * question you found hard. This is a claim that the question is *wrong*, and it has a different
 * destination: it travels with the exported results and feeds the corrected-exam export.
 *
 * Nothing here is ever transmitted. The dialog says so, because a "report" button in almost every
 * other piece of software means "send this to someone".
 */
export function ReportQuestionDialog({
  questionId,
  existing,
  onSave,
  onCancel,
}: {
  readonly questionId: QuestionId;
  readonly existing: QuestionReport | undefined;
  readonly onSave: (report: QuestionReport) => void;
  readonly onCancel: () => void;
}) {
  const { t } = useI18n();
  const [reasons, setReasons] = useState<readonly ReportReason[]>(existing?.reasons ?? []);
  const [comment, setComment] = useState(existing?.comment ?? '');
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onCancel]);

  const toggle = (reason: ReportReason): void => {
    setReasons((current) =>
      current.includes(reason) ? current.filter((entry) => entry !== reason) : [...current, reason],
    );
  };

  return (
    <div className="dialog-backdrop" role="presentation">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="report-question-title">
        <h2 id="report-question-title">{t('reportQuestion.title')}</h2>
        <p className="field__help">{t('reportQuestion.help')}</p>

        <fieldset className="checkbox-group">
          <legend className="visually-hidden">{t('reportQuestion.title')}</legend>
          {REASONS.map((reason, index) => (
            <label key={reason} className="checkbox">
              <input
                ref={index === 0 ? firstRef : undefined}
                type="checkbox"
                checked={reasons.includes(reason)}
                onChange={() => {
                  toggle(reason);
                }}
              />
              <span>{t(`reportQuestion.reason.${reason}` as const)}</span>
            </label>
          ))}
        </fieldset>

        <label className="field">
          <span>{t('reportQuestion.comment')}</span>
          <textarea
            rows={3}
            maxLength={2000}
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);
            }}
          />
        </label>

        <div className="actions">
          <Button
            variant="primary"
            disabled={reasons.length === 0}
            onClick={() => {
              onSave({
                questionId,
                reasons,
                ...(comment.trim() !== '' && { comment: comment.trim() }),
                createdAt: new Date().toISOString(),
              });
            }}
          >
            {t('reportQuestion.save')}
          </Button>
          <Button onClick={onCancel}>{t('reportQuestion.cancel')}</Button>
        </div>
      </div>
    </div>
  );
}
